import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScanFace, Clock, MapPin } from "lucide-react";
import { CameraCapture } from "@/components/CameraCapture";
import { getCurrentLocation, fmtTime, errorMsg } from "@/lib/utils-app";
import { toast } from "sonner";

/**
 * HourlySelfieBanner — ITCOT/PMC supervisor hourly self-attendance.
 *
 * Polls /pmc/hourly-selfie/state every 60s. When at least one assigned park
 * is due for a fresh check-in inside the working window, surfaces a banner
 * with a single CTA. Clicking it opens the live front-camera capture sheet
 * and submits to /pmc/hourly-selfie.
 *
 * Designed to be drop-in on top of any PMC page (we put it on /pmc/today).
 * Silent when nothing is due, when the feature is disabled, or when the
 * supervisor is outside the configured window for all of their parks.
 */
export function HourlySelfieBanner() {
  const [state, setState] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedParkId, setSelectedParkId] = useState("");
  const [photo, setPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/pmc/hourly-selfie/state");
      setState(data);
    } catch {
      // silent — feature is non-critical
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Choose a sensible default park for the dialog: first DUE park, else first park.
  useEffect(() => {
    if (!state?.parks?.length) return;
    const due = state.parks.find((p) => p.due);
    setSelectedParkId((cur) => cur || (due?.park_id ?? state.parks[0].park_id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const duePark = useMemo(
    () => state?.parks?.find((p) => p.due) || null,
    [state],
  );

  const everEnabled = useMemo(
    () => state?.parks?.some((p) => p.enabled && p.within_window) || false,
    [state],
  );

  const submit = async () => {
    if (!photo) return toast.error("Please capture a selfie before submitting");
    if (!selectedParkId) return toast.error("Pick a park");
    setSubmitting(true);
    try {
      // GPS is REQUIRED for the hourly check-in. Without coordinates we
      // can't validate the supervisor is inside the park geofence, so we
      // block the submit and surface a clear, actionable error.
      let loc = null;
      try {
        loc = await getCurrentLocation();
      } catch {
        loc = null;
      }
      if (!loc || loc.lat == null || loc.long == null) {
        toast.error(
          "Location is required. Please allow GPS / location permission for this site and try again.",
        );
        setSubmitting(false);
        return;
      }
      await api.post("/pmc/hourly-selfie", {
        park_id: selectedParkId,
        photo_b64: photo,
        gps_lat: loc.lat,
        gps_long: loc.long,
      });
      toast.success("Hourly check-in submitted");
      setPhoto(null);
      setDialogOpen(false);
      await refresh();
    } catch (e) {
      toast.error(errorMsg(e, "Failed to submit"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!state) return null;
  // Use supervisor-level fields (new) with graceful fallback to legacy
  // per-park aggregation so older clients keep working.
  const supEnabled = state.enabled ?? (state.parks?.some((p) => p.enabled && p.within_window) || false);
  const supDue = state.due ?? !!duePark;
  const supInterval = state.interval_minutes ?? (state.parks?.[0]?.interval_minutes || null);
  const supNextDue = state.next_due_minutes ?? state.parks?.find((p) => p.next_due_minutes != null)?.next_due_minutes ?? null;
  if (!supEnabled && !everEnabled) return null;

  return (
    <>
      <Card
        className={supDue
          ? "border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50/80"
          : "border-emerald-200 bg-emerald-50/50"}
        data-testid="pmc-hourly-banner"
      >
        <CardContent className="flex flex-wrap items-center gap-3 p-3">
          <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-full ${supDue ? "bg-amber-200 text-amber-900" : "bg-emerald-200 text-emerald-900"}`}>
            <ScanFace className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
              {supDue ? (
                <span className="text-amber-900">Hourly check-in pending</span>
              ) : (
                <span className="text-emerald-900">Hourly check-in up to date</span>
              )}
            </div>
            <div className="mt-0.5 text-[11px] text-slate-600">
              <Clock className="-mt-0.5 mr-0.5 inline h-3 w-3" />
              Every {supInterval || 60}m
              {!supDue && supNextDue != null ? <span className="ml-2">· next in {supNextDue}m</span> : null}
            </div>
          </div>
          {supDue && (
            <Button
              size="sm"
              onClick={() => setDialogOpen(true)}
              className="bg-amber-600 text-white hover:bg-amber-700"
              data-testid="pmc-hourly-open"
            >
              <ScanFace className="mr-1.5 h-4 w-4" />
              Capture now
            </Button>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onOpenChange={(o) => {
          if (submitting) return;
          setDialogOpen(o);
          if (!o) setPhoto(null);
        }}
      >
        <DialogContent className="max-w-md" data-testid="pmc-hourly-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScanFace className="h-4 w-4" /> Hourly check-in
            </DialogTitle>
            <DialogDescription className="text-xs">
              Take a quick selfie to mark your presence. This is separate from
              worker attendance — only your photo is captured.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-[11px] uppercase tracking-wide text-slate-500">
                Park
              </label>
              <Select value={selectedParkId} onValueChange={setSelectedParkId}>
                <SelectTrigger data-testid="pmc-hourly-park">
                  <SelectValue placeholder="Pick a park" />
                </SelectTrigger>
                <SelectContent>
                  {(state.parks || []).map((p) => (
                    <SelectItem key={p.park_id} value={p.park_id}>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-3 w-3 text-muted-foreground" />
                        <span className="font-medium">{p.park_name}</span>
                        <span className="text-[10px] text-muted-foreground">
                          {p.interval_minutes}m
                          {p.due ? " · DUE" : (p.next_due_minutes != null ? ` · next in ${p.next_due_minutes}m` : "")}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <CameraCapture
              onCapture={(b64) => setPhoto(b64)}
              onClear={() => setPhoto(null)}
              captured={photo}
              facingMode="user"
              showFlip
              testIdPrefix="pmc-hourly-cam"
              overlayDateTime
              helperText="Face the camera and tap Capture."
            />

            {photo ? (
              <div className="rounded-md bg-slate-50 px-2 py-1.5 text-[11px] text-slate-600">
                <Clock className="-mt-0.5 mr-0.5 inline h-3 w-3" /> Will be timestamped at {fmtTime(new Date())} IST.
              </div>
            ) : null}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDialogOpen(false);
                setPhoto(null);
              }}
              disabled={submitting}
              data-testid="pmc-hourly-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={submitting || !photo || !selectedParkId}
              data-testid="pmc-hourly-submit"
            >
              {submitting ? "Saving…" : "Submit check-in"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
